/**
 * useQuoteBuilder.js — Quote builder state management
 *
 * Single useReducer managing all quote builder state:
 * - Quote mode (RE / Custom)
 * - Client info (name, email, phone, contact, address, sqft)
 * - Selected services (RE mode)
 * - Line items (both modes)
 * - Add-on selections
 * - Shoot dates
 * - Discount (type, value, reason)
 * - Terms selections
 * - Quote metadata (number, date, validity, prepared-by, originator)
 *
 * Exposes computed values: subtotal, discountAmount, total, estimated duration,
 * selected category IDs, active line items.
 */

import { useReducer, useMemo, useCallback, useEffect } from 'react';
import {
  rgFindProduct,
  rgFindAddon,
  rgGetPrice,
  rgAddonPrice,
  rgPriceContext,
  rgBillableAddonIds,
  rgNormalizeVersion,
  RG_CURRENT_VERSION,
  RG_LEGACY_VERSION,
} from '../data/serviceCatalog';
import {
  vtGetPrice,
  getVtAddonPrice,
  vtGetPid,
  vtGetPackagePrice,
  vtGetName,
  vtNormalizeVersion,
  vtExistsAt,
  VT_CURRENT_VERSION,
  VT_LEGACY_VERSION,
  findVtProduct,
  findVtPackage,
} from '../data/virtourCatalog';
import { DEFAULT_TERMS } from '../data/defaultTerms';
import { snapshotTerms, isLegacyTerms, getEffectiveLibrary } from '../lib/quoteTerms';
import { vtLinePayout, vtTotals } from '../lib/virtourQuote';
import { useSettings } from './useSettings';
import { uid, toLocalDateStr } from '../lib/utils';
import { lineSid, addonSid } from '../lib/calcEngine';

// ── Stable line identity (sid) ──────────────────────────────────────────────
// Every line carries a persistent string `sid` (distinct from the builder-local
// integer `id`). Pay allocations (calc_snapshot.itemSplits / itemAllocs) key on
// it, so it must survive requotes, reloads, and quote→job graduation.
// New lines get a random sid; legacy lines (saved before sids existed) are
// backfilled DETERMINISTICALLY via lineSid(line, nonAddonIndex) — the same
// derivation the calculator uses — so both sides mint identical ids.
function genSid() {
  return 'li_' + Math.random().toString(16).slice(2, 10).padEnd(8, '0');
}

/** Backfill sids on a line array in place-order (re-addon lines get random sids —
 *  they never reach calc_snapshot.deliverableLines). */
function backfillSids(lines) {
  let nonAddonIdx = 0;
  return (lines || []).map((l) => {
    const isAddon = l?.source === 're-addon';
    const idx = nonAddonIdx;
    if (!isAddon) nonAddonIdx++;
    if (l?.sid) return l;
    return { ...l, sid: isAddon ? genSid() : lineSid(l, idx) };
  });
}

// ── Constants ──────────────────────────────────────────────────────────────
const DRAFT_KEY = 'rfos_quote_draft';

// ── Helpers ────────────────────────────────────────────────────────────────

/** Add N business days (skips Sat/Sun) to a YYYY-MM-DD string */
function addBusinessDays(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return toLocalDateStr(d);
}

/** Get the last date from a schedule array (accounting for endDate ranges) */
function getLastScheduleDate(schedule) {
  let last = '';
  for (const s of schedule) {
    const d = s.endDate || s.date || '';
    if (d > last) last = d;
  }
  return last;
}

/** Add minutes to a HH:MM string, return HH:MM or '' on overflow past 23:59 */
function estimateEndTime(startTime, durationMinutes) {
  if (!startTime || !durationMinutes) return '';
  const [h, m] = startTime.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return '';
  const total = h * 60 + m + durationMinutes;
  if (total >= 24 * 60) return '23:59';
  const rounded = Math.round(total / 5) * 5;
  if (rounded >= 24 * 60) return '23:59';
  const hh = String(Math.floor(rounded / 60)).padStart(2, '0');
  const mm = String(rounded % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

// ── Initial State ──────────────────────────────────────────────────────────

function createInitialState() {
  // Skip sessionStorage restore if navigating to a specific job or quote
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.has('jobId') || params.has('quoteId')) {
      return freshState();
    }
  }

  // Try restoring from sessionStorage
  try {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.client) {
        // Clear stale quote number so it regenerates from current DB state
        parsed.quoteNumber = '';
        // Heal nextLineId in case a pre-fix draft left it stale (would cause
        // ADD_LINE to collide with existing line ids and silently target the
        // wrong line on edit/remove).
        if (Array.isArray(parsed.lines) && parsed.lines.length > 0) {
          const maxId = parsed.lines.reduce((m, l) => Math.max(m, Number(l.id) || 0), 0);
          parsed.nextLineId = Math.max(Number(parsed.nextLineId) || 1, maxId + 1);
          // Heal sids on pre-sid drafts
          parsed.lines = backfillSids(parsed.lines);
        }
        // Heal per-quote terms fields added in PR 2 — drafts saved before that
        // PR shipped lack them, which would crash the new reducer actions.
        if (!parsed.termOverrides || typeof parsed.termOverrides !== 'object') {
          parsed.termOverrides = {};
        }
        if (!Array.isArray(parsed.customTerms)) parsed.customTerms = [];
        if (!Array.isArray(parsed.termOrder)) parsed.termOrder = [];
        // Heal workflow_type on pre-feature drafts (undefined would round-trip
        // to a NULL server-side, bypassing the column DEFAULT 'standard').
        if (parsed.workflowType == null) parsed.workflowType = 'standard';
        // A draft from before the RE price lists split with RE picks on it was
        // priced off the Old list, so it stays there rather than repricing
        // under the user. Anything else (a VirTour or custom draft, an empty
        // one) has nothing priced yet and starts on New like a fresh quote.
        if (parsed.rePriceVersion == null) {
          const rePriced = parsed.mode === 're' && Object.keys(parsed.selectedProducts || {}).length > 0;
          parsed.rePriceVersion = rePriced ? RG_LEGACY_VERSION : RG_CURRENT_VERSION;
        }
        if (!Number.isFinite(parsed.nextCustomTermId)) {
          const maxCustom = parsed.customTerms.reduce((m, t) => {
            const match = String(t?.id || '').match(/^custom_(\d+)$/);
            return match ? Math.max(m, parseInt(match[1], 10)) : m;
          }, 0);
          parsed.nextCustomTermId = maxCustom + 1;
        }
        return parsed;
      }
    }
  } catch { /* ignore */ }

  return freshState();
}

function freshState() {
  const today = toLocalDateStr(new Date());
  const validDate = toLocalDateStr(new Date(Date.now() + 30 * 86400000));

  return {
    mode: 're', // 're', 'virtour', or 'custom'
    workflowType: 'standard', // 'standard' | 'staycation'

    // Client info
    // contactId is the durable link to the CRM row; `client` is only the label
    // rendered at the time of picking. A contact renamed later is re-resolved
    // from this id at save time, so a stale label can never be written back.
    contactId: '',
    client: '',
    contact: '',
    email: '',
    phone: '',
    address: '',
    sqft: '',
    propertyValue: '', // for film pricing

    // Property info (RE mode)
    propertyType: '',
    bedrooms: '',
    bathrooms: '',

    // Notes
    notes: '',          // quote notes — appears on PDF
    internalNotes: '',   // internal-only — saved to job, NOT on PDF

    // Quote metadata
    quoteNumber: '',
    quoteDate: today,
    validUntil: validDate,
    preparedBy: '',
    originator: '',
    projectName: '', // custom mode only

    // RE mode: selected product IDs
    selectedProducts: {}, // { productId: true }
    // RE mode: how many of a product ship, where more than one (three Dynamic
    // Reels from a website order link). Absent means one.
    productQuantities: {},
    // RE mode: which price list the whole quote sells from ('current' = New,
    // 'legacy' = Old). New quotes start on New; a loaded quote keeps its own.
    rePriceVersion: RG_CURRENT_VERSION,

    // Add-on state: { addonId: true } or { addonId: quantity } for per-image
    selectedAddons: {},
    addonQuantities: {}, // { addonId: number } for per-image add-ons

    // Line items (both modes use this)
    lines: [],
    nextLineId: 1,

    // What this quote looked like before it was converted to another mode.
    // Read-only reference while rebuilding from a catalog — never priced.
    // { quoteNumber, mode, total, lines: [{ desc, qty, rate, amount }] }
    convertedFrom: null,

    // Shoot dates
    shootDates: [],
    nextShootDateId: 1,

    // Discount
    discountType: 'pct', // 'pct' or 'flat'
    discountValue: 0,
    discountReason: '',

    // Terms: array of { id, on } — initialized from DEFAULT_TERMS
    terms: DEFAULT_TERMS.map((t) => ({ id: t.id, on: t.on })),
    // Per-quote overrides: { [termId]: { label?, body? } } — patches a library
    // term's display only for this quote (does NOT mutate the library).
    termOverrides: {},
    // Per-quote custom clauses: [{ id, label, body, on }] — ad-hoc terms only
    // on this quote, never persisted to the library.
    customTerms: [],
    // Display order: string[] of term IDs (library + custom). When empty, the
    // builder falls back to library order then custom-append order.
    termOrder: [],
    nextCustomTermId: 1,

    // Linked job (for re-quoting)
    linkedJobId: null,
    sourceQuoteId: null,
    sourceQuoteRevision: 1,
    sourceQuoteStatus: null,
  };
}

// ── Reducer ────────────────────────────────────────────────────────────────

function quoteReducer(state, action) {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };

    // Switching an existing quote into a catalog-priced mode: the old lines
    // stop being the quote and become the reference you rebuild against, so
    // the numbers you replace them with stay visible while you work.
    case 'CONVERT_MODE': {
      const keep = state.lines.filter((l) => l.desc && (l.rate > 0 || l.source === 'custom'));
      const snapshot = keep.length
        ? {
            quoteNumber: state.quoteNumber || '',
            mode: state.mode,
            total: keep.reduce((s, l) => s + (l.qty || 0) * (l.rate || 0), 0),
            lines: keep.map((l) => ({
              desc: l.desc,
              detail: l.detail || '',
              qty: l.qty || 1,
              rate: l.rate || 0,
              amount: (l.qty || 0) * (l.rate || 0),
            })),
          }
        : null;
      return {
        ...state,
        mode: action.payload,
        lines: [],
        selectedProducts: {},
        productQuantities: {},
        selectedAddons: {},
        addonQuantities: {},
        // A conversion rebuilds from the catalog, so a rebuilt RE quote starts
        // on the New list like any new quote.
        rePriceVersion: action.payload === 're' ? RG_CURRENT_VERSION : state.rePriceVersion,
        // Keep the earliest reference: converting twice shouldn't overwrite
        // the original quote with an empty intermediate state.
        convertedFrom: snapshot || state.convertedFrom,
      };
    }

    // ── RE price list (the Old / New tab) ──
    // Moves the whole quote to the other list. Picks the target list doesn't
    // sell are dropped (New-only items going to Old, and the Old add-ons New
    // retired, e.g. Video Length Upgrade); the line rebuild then reprices
    // everything at the new list, and the switch dialog shows both totals.
    case 'SET_RE_PRICE_VERSION': {
      const version = rgNormalizeVersion(action.priceVersion);
      if (version === rgNormalizeVersion(state.rePriceVersion)) return state;
      const keepIds = (obj, exists) => Object.fromEntries(
        Object.entries(obj || {}).filter(([id]) => exists(id))
      );
      return {
        ...state,
        rePriceVersion: version,
        selectedProducts: keepIds(state.selectedProducts, (id) => !!rgFindProduct(id, version)),
        productQuantities: keepIds(state.productQuantities, (id) => !!rgFindProduct(id, version)),
        selectedAddons: keepIds(state.selectedAddons, (id) => !!rgFindAddon(id, version)),
        addonQuantities: keepIds(state.addonQuantities, (id) => !!rgFindAddon(id, version)),
      };
    }

    case 'CLEAR_CONVERTED_FROM':
      return { ...state, convertedFrom: null };

    case 'SET_FIELD': {
      const newState = { ...state, [action.field]: action.value };
      // When address changes, update lines whose location is empty or matched previous address.
      // Updates BOTH the legacy top-level `l.location` and `l.schedule[0].location` so the
      // active per-line schedule (which drives shoot rendering) stays in sync.
      if (action.field === 'address' && state.lines.length > 0) {
        const oldAddr = state.address || '';
        const newAddr = action.value || '';
        newState.lines = state.lines.map((l) => {
          let next = l;
          if (!l.location || l.location === oldAddr) {
            next = { ...next, location: newAddr };
          }
          const sched = l.schedule || [];
          if (sched.length > 0) {
            const s0 = sched[0] || {};
            const cur = s0.location || '';
            if (!cur || cur === oldAddr) {
              const newSched = [...sched];
              newSched[0] = { ...s0, location: newAddr };
              next = { ...next, schedule: newSched };
            }
          }
          return next;
        });
      }
      return newState;
    }

    case 'SET_CLIENT_INFO':
      return { ...state, ...action.payload };

    // ── RE Product Selection ──
    // The stored value is the price version the item sells at, not `true`. Both
    // versions are truthy, so every `selectedProducts[id]` check still reads as
    // "is it selected". A catalog with one price set (RE) passes no version and
    // gets the plain on/off toggle it always had.
    case 'TOGGLE_PRODUCT': {
      const { productId, priceVersion } = action;
      const selected = { ...state.selectedProducts };
      const current = selected[productId];
      if (!current) {
        selected[productId] = priceVersion || VT_CURRENT_VERSION;
      } else if (!priceVersion || vtNormalizeVersion(current) === vtNormalizeVersion(priceVersion)) {
        // Clicking the column it already sells at turns it off.
        delete selected[productId];
      } else {
        // Clicking the other column reprices it rather than dropping it.
        selected[productId] = vtNormalizeVersion(priceVersion);
      }
      // A product unticked loses its count; ticked again, it is one.
      if (!selected[productId] && state.productQuantities?.[productId]) {
        const productQuantities = { ...state.productQuantities };
        delete productQuantities[productId];
        return { ...state, selectedProducts: selected, productQuantities };
      }
      return { ...state, selectedProducts: selected };
    }

    case 'CLEAR_PRODUCTS':
      return { ...state, selectedProducts: {}, productQuantities: {}, selectedAddons: {}, addonQuantities: {} };

    // ── A website order link (lib/siteOrderLink: siteOrderToQuote) ──
    // REPLACES the quote's Real Estate picks with the link's, on the New list,
    // and takes the link's size when it has one (Cyprian, 2026-10-03: replace,
    // never merge; client, address and dates stay). A line whose product or
    // add-on is imported again keeps its place, sid and schedule, but not a
    // rate typed by hand: the rebuild that follows prices it fresh. Every other
    // Real Estate line goes; custom lines stay.
    case 'IMPORT_SITE_SELECTION': {
      const sel = action.selection;
      const picked = new Set([...Object.keys(sel.selectedProducts), ...Object.keys(sel.selectedAddons)]);
      const isRe = (l) => l.source === 're' || l.source === 're-addon';
      const lines = state.lines
        .filter((l) => !isRe(l) || picked.has(l.productId))
        .map((l) => (isRe(l) && l.rateOverride
          ? { ...l, rateOverride: false, originalRate: undefined, originalSqft: undefined }
          : l));
      return {
        ...state,
        ...(sel.sqft ? { sqft: sel.sqft } : {}),
        rePriceVersion: RG_CURRENT_VERSION,
        selectedProducts: { ...sel.selectedProducts },
        productQuantities: { ...sel.productQuantities },
        selectedAddons: { ...sel.selectedAddons },
        addonQuantities: { ...sel.addonQuantities },
        lines,
      };
    }

    // ── Add-ons ──
    case 'TOGGLE_ADDON': {
      const { addonId } = action;
      const addons = { ...state.selectedAddons };
      if (addons[addonId]) {
        delete addons[addonId];
        const qtys = { ...state.addonQuantities };
        delete qtys[addonId];
        return { ...state, selectedAddons: addons, addonQuantities: qtys };
      }
      addons[addonId] = true;
      return { ...state, selectedAddons: addons };
    }

    case 'SET_ADDON_QTY': {
      const { addonId, qty } = action;
      return { ...state, addonQuantities: { ...state.addonQuantities, [addonId]: qty } };
    }

    // ── Line Items ──
    case 'ADD_LINE': {
      const newLine = {
        id: state.nextLineId,
        sid: action.sid || genSid(),
        desc: action.desc || '',
        detail: action.detail || '',
        qty: action.qty ?? 1,
        rate: action.rate ?? 0,
        dur: action.dur ?? 0,
        source: action.source || state.mode,
        productId: action.productId || null,
        categoryId: action.categoryId || null,
        schedule: action.schedule || [{
          date: action.shootDate || '',
          endDate: '',
          time: action.shootTime || '',
          endTime: action.endTime || '',
          location: action.location || '',
        }],
        deliveryDate: action.deliveryDate || '',
        deliveryTime: action.deliveryTime || 'EOD',
        shootDateId: action.shootDateId || null,
      };
      return {
        ...state,
        lines: [...state.lines, newLine],
        nextLineId: state.nextLineId + 1,
      };
    }

    case 'UPDATE_SCHEDULE': {
      const { lineId, entryIdx, updates: schedUpdates } = action;
      const li = state.lines.findIndex((l) => l.id === lineId);
      if (li === -1) return state;
      const line = state.lines[li];
      const schedule = [...(line.schedule || [])];
      if (entryIdx < 0 || entryIdx >= schedule.length) return state;
      schedule[entryIdx] = { ...schedule[entryIdx], ...schedUpdates };
      // Auto-estimate endTime for RE lines when start time changes
      if ('time' in schedUpdates && line.source === 're' && line.dur) {
        schedule[entryIdx] = { ...schedule[entryIdx], endTime: estimateEndTime(schedule[entryIdx].time, line.dur) };
      }
      const updatedLine = { ...line, schedule };
      // Auto-set delivery from last schedule date
      const lastDate = getLastScheduleDate(schedule);
      if (lastDate && !updatedLine.deliveryDate) {
        const delivery = addBusinessDays(lastDate, 3);
        if (delivery) updatedLine.deliveryDate = delivery;
      }
      // Cascade schedule[0] fields to subsequent lines with empty/matching schedule[0]
      const newLines = [...state.lines];
      newLines[li] = updatedLine;
      if (entryIdx === 0) {
        const oldEntry = (line.schedule || [])[0] || {};
        const newEntry = schedule[0];
        for (const field of ['date', 'time', 'location']) {
          if (!(field in schedUpdates)) continue;
          const oldVal = oldEntry[field] || '';
          const newVal = newEntry[field] || '';
          if (oldVal === newVal) continue;
          for (let i = li + 1; i < newLines.length; i++) {
            const s0 = (newLines[i].schedule || [])[0];
            if (!s0) continue;
            const curVal = s0[field] || '';
            if (curVal === '' || curVal === oldVal) {
              const newSched = [...(newLines[i].schedule || [])];
              newSched[0] = { ...newSched[0], [field]: newVal };
              newLines[i] = { ...newLines[i], schedule: newSched };
              if (field === 'date' && newVal && !newLines[i].deliveryDate) {
                const delivery = addBusinessDays(newVal, 3);
                if (delivery) newLines[i] = { ...newLines[i], deliveryDate: delivery };
              }
            }
          }
        }
      }
      return { ...state, lines: newLines };
    }

    case 'ADD_SCHEDULE_ENTRY': {
      const li2 = state.lines.findIndex((l) => l.id === action.lineId);
      if (li2 === -1) return state;
      const line2 = state.lines[li2];
      const newEntry = { date: '', endDate: '', time: '', endTime: '', location: '' };
      const newLines2 = [...state.lines];
      newLines2[li2] = { ...line2, schedule: [...(line2.schedule || []), newEntry] };
      return { ...state, lines: newLines2 };
    }

    case 'REMOVE_SCHEDULE_ENTRY': {
      const li3 = state.lines.findIndex((l) => l.id === action.lineId);
      if (li3 === -1) return state;
      const line3 = state.lines[li3];
      const sched3 = (line3.schedule || []).filter((_, i) => i !== action.entryIdx);
      if (sched3.length === 0) return state; // keep at least 1
      const newLines3 = [...state.lines];
      newLines3[li3] = { ...line3, schedule: sched3 };
      return { ...state, lines: newLines3 };
    }

    case 'UPDATE_LINE': {
      const { lineId, updates } = action;
      const CASCADE_FIELDS = ['deliveryDate', 'deliveryTime'];
      const lineIdx = state.lines.findIndex((l) => l.id === lineId);
      if (lineIdx === -1) return state;

      const oldLine = state.lines[lineIdx];
      let updatedLine = { ...oldLine, ...updates };

      // Track price overrides on catalog-priced lines
      if ('rate' in updates && (oldLine.source === 're' || oldLine.source === 're-addon' || oldLine.source === 'virtour')) {
        const newRate = parseFloat(updates.rate) || 0;
        const origRate = oldLine.originalRate != null ? oldLine.originalRate : oldLine.rate;
        if (newRate !== origRate) {
          updatedLine.rateOverride = true;
          if (oldLine.originalRate == null) {
            updatedLine.originalRate = oldLine.rate;
            updatedLine.originalSqft = parseInt(state.sqft) || 0;
          }
        } else {
          // User typed back the catalog price — clear override
          updatedLine.rateOverride = false;
          updatedLine.originalRate = undefined;
          updatedLine.originalSqft = undefined;
        }
      }

      const newLines = [...state.lines];
      newLines[lineIdx] = updatedLine;

      // Cascade: for each changed cascadable field, update subsequent lines
      // whose value is empty or matches the old value
      for (const field of CASCADE_FIELDS) {
        if (!(field in updates)) continue;
        const oldVal = oldLine[field] || '';
        const newVal = updatedLine[field] || '';
        if (oldVal === newVal) continue;

        for (let i = lineIdx + 1; i < newLines.length; i++) {
          const curVal = newLines[i][field] || '';
          if (curVal === '' || curVal === oldVal) {
            newLines[i] = { ...newLines[i], [field]: newVal };
          }
        }
      }

      return { ...state, lines: newLines };
    }

    case 'REMOVE_LINE':
      return { ...state, lines: state.lines.filter((l) => l.id !== action.lineId) };

    case 'MOVE_LINE': {
      const { fromIndex, toIndex } = action;
      if (fromIndex === toIndex) return state;
      if (fromIndex < 0 || fromIndex >= state.lines.length) return state;
      if (toIndex < 0 || toIndex >= state.lines.length) return state;
      const next = [...state.lines];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { ...state, lines: next };
    }

    case 'RESET_LINE_RATE': {
      const rlIdx = state.lines.findIndex((l) => l.id === action.lineId);
      if (rlIdx === -1) return state;
      const rl = state.lines[rlIdx];
      if (!rl.rateOverride || rl.originalRate == null) return state;
      // A size-priced add-on resets to its price at the quote's size now, not
      // the one remembered when the rate was typed: a reopened quote doesn't
      // rebuild until a pick or the size changes, so its line can still carry
      // an override typed at another size. Where that size is quoted (the 3D
      // plan at 10,000+) it goes back to the amber Quote state, never a bare $0
      // that leaves the line off the quote unseen.
      const rlAddon = rl.source === 're-addon' ? rgFindAddon(rl.productId, state.rePriceVersion) : null;
      const sizePrice = rlAddon?.sizePrices ? rgAddonPrice(rlAddon, parseInt(state.sqft) || 0) : undefined;
      const reset = sizePrice === undefined
        ? { rate: rl.originalRate }
        : { rate: sizePrice ?? 0, quoted: sizePrice == null };
      const resetLines = [...state.lines];
      resetLines[rlIdx] = { ...rl, ...reset, rateOverride: false, originalRate: undefined, originalSqft: undefined };
      return { ...state, lines: resetLines };
    }

    case 'SET_LINES': {
      const maxId = action.lines.reduce((m, l) => Math.max(m, Number(l.id) || 0), 0);
      return { ...state, lines: action.lines, nextLineId: Math.max(state.nextLineId, maxId + 1) };
    }

    case 'CLEAR_LINES':
      return { ...state, lines: [], nextLineId: 1 };

    // ── Shoot Dates ──
    case 'ADD_SHOOT_DATE': {
      const newSD = {
        id: state.nextShootDateId,
        date: '',
        label: '',
        startTime: '09:00',
      };
      return {
        ...state,
        shootDates: [...state.shootDates, newSD],
        nextShootDateId: state.nextShootDateId + 1,
      };
    }

    case 'UPDATE_SHOOT_DATE': {
      const { sdId, updates } = action;
      const oldSD = state.shootDates.find((sd) => sd.id === sdId) || {};
      const newSD = { ...oldSD, ...updates };
      // Cascade date/startTime into per-line schedule[0] for any line assigned
      // to this shoot date. Only overwrites when the line's current value is
      // empty or matches the previous shoot-date value, so manual per-line
      // overrides are preserved.
      const cascade = (l) => {
        if (l.shootDateId !== sdId) return l;
        const sched = l.schedule && l.schedule.length > 0
          ? [...l.schedule]
          : [{ date: '', endDate: '', time: '', endTime: '', location: '' }];
        const s0 = { ...sched[0] };
        if ('date' in updates) {
          const cur = s0.date || '';
          if (!cur || cur === (oldSD.date || '')) s0.date = newSD.date || '';
        }
        if ('startTime' in updates) {
          const cur = s0.time || '';
          if (!cur || cur === (oldSD.startTime || '')) s0.time = newSD.startTime || '';
        }
        sched[0] = s0;
        return { ...l, schedule: sched };
      };
      return {
        ...state,
        shootDates: state.shootDates.map((sd) => (sd.id === sdId ? newSD : sd)),
        lines: state.lines.map(cascade),
      };
    }

    case 'REMOVE_SHOOT_DATE': {
      const removedId = action.sdId;
      return {
        ...state,
        shootDates: state.shootDates.filter((sd) => sd.id !== removedId),
        lines: state.lines.map((l) =>
          l.shootDateId === removedId ? { ...l, shootDateId: null } : l
        ),
      };
    }

    // ── Discount ──
    case 'SET_DISCOUNT':
      return {
        ...state,
        discountType: action.discountType ?? state.discountType,
        discountValue: action.discountValue ?? state.discountValue,
        discountReason: action.discountReason ?? state.discountReason,
      };

    // ── Terms ──
    case 'TOGGLE_TERM': {
      const { termId } = action;
      // Custom terms: toggle the inline `on` field
      if (termId && termId.startsWith('custom_')) {
        const current = Array.isArray(state.customTerms) ? state.customTerms : [];
        return {
          ...state,
          customTerms: current.map((t) =>
            t.id === termId ? { ...t, on: !t.on } : t
          ),
        };
      }
      // Library terms: toggle in the state.terms array. If the term isn't
      // there yet (library-added term not initialised in createInitialState),
      // append it as on=true so the toggle takes effect on first click.
      // `userSet: true` marks an explicit manual choice so the activeTerms memo
      // honors it for conditional terms regardless of selected service categories.
      const existing = state.terms.find((t) => t.id === termId);
      if (!existing) {
        return {
          ...state,
          terms: [...state.terms, { id: termId, on: true, userSet: true }],
        };
      }
      return {
        ...state,
        terms: state.terms.map((t) => (t.id === termId ? { ...t, on: !t.on, userSet: true } : t)),
      };
    }

    case 'SET_TERMS':
      return { ...state, terms: action.terms };

    // Per-quote override: patch a library term's label/body for this quote only.
    case 'SET_TERM_OVERRIDE': {
      const { termId, label, body, note } = action;
      if (!termId) return state;
      const overrides = (state.termOverrides && typeof state.termOverrides === 'object')
        ? state.termOverrides
        : {};
      const existing = overrides[termId] || {};
      const next = { ...existing };
      if (label !== undefined) next.label = label;
      if (body !== undefined) next.body = body;
      if (note !== undefined) next.note = note;
      return {
        ...state,
        termOverrides: { ...overrides, [termId]: next },
      };
    }

    // Remove a per-quote override (restores library default).
    case 'CLEAR_TERM_OVERRIDE': {
      const { termId } = action;
      const overrides = (state.termOverrides && typeof state.termOverrides === 'object')
        ? state.termOverrides
        : {};
      if (!termId || !overrides[termId]) return state;
      const { [termId]: _removed, ...rest } = overrides;
      return { ...state, termOverrides: rest };
    }

    // Add a custom (ad-hoc) clause that lives only on this quote.
    case 'ADD_CUSTOM_TERM': {
      const { label = '', body = '', note = '' } = action;
      const customs = Array.isArray(state.customTerms) ? state.customTerms : [];
      const order = Array.isArray(state.termOrder) ? state.termOrder : [];
      // Heal nextCustomTermId if NaN/missing by scanning existing ids.
      const safeNext = Number.isFinite(state.nextCustomTermId)
        ? state.nextCustomTermId
        : customs.reduce((m, t) => {
            const match = String(t?.id || '').match(/^custom_(\d+)$/);
            return match ? Math.max(m, parseInt(match[1], 10)) : m;
          }, 0) + 1;
      const id = `custom_${safeNext}`;
      const newTerm = { id, label, body, note, on: true };
      return {
        ...state,
        customTerms: [...customs, newTerm],
        nextCustomTermId: safeNext + 1,
        // Append to termOrder if order has been customized; otherwise leave
        // empty so default ordering still works.
        termOrder: order.length > 0 ? [...order, id] : order,
      };
    }

    case 'UPDATE_CUSTOM_TERM': {
      const { id, label, body, note } = action;
      const customs = Array.isArray(state.customTerms) ? state.customTerms : [];
      return {
        ...state,
        customTerms: customs.map((t) =>
          t.id === id
            ? {
                ...t,
                ...(label !== undefined ? { label } : {}),
                ...(body !== undefined ? { body } : {}),
                ...(note !== undefined ? { note } : {}),
              }
            : t
        ),
      };
    }

    case 'REMOVE_CUSTOM_TERM': {
      const { id } = action;
      const customs = Array.isArray(state.customTerms) ? state.customTerms : [];
      const order = Array.isArray(state.termOrder) ? state.termOrder : [];
      return {
        ...state,
        customTerms: customs.filter((t) => t.id !== id),
        termOrder: order.filter((tid) => tid !== id),
      };
    }

    // Promote a custom (per-quote) clause to a library term reference. The
    // library write itself happens in the parent (settings mutation); this
    // action just swaps the local in-state representation: drop from
    // customTerms, add to terms, and patch termOrder in place if present.
    case 'PROMOTE_CUSTOM_TO_LIBRARY': {
      const { customId, libraryId } = action;
      if (!customId || !libraryId) return state;
      const customs = Array.isArray(state.customTerms) ? state.customTerms : [];
      const existing = customs.find((t) => t.id === customId);
      // Preserve the on/off state from the custom term (default true if absent)
      const on = existing ? existing.on !== false : true;
      const terms = Array.isArray(state.terms) ? state.terms : [];
      const alreadyRef = terms.some((t) => t.id === libraryId);
      const nextTerms = alreadyRef
        ? terms.map((t) => (t.id === libraryId ? { ...t, on } : t))
        : [...terms, { id: libraryId, on }];
      const order = Array.isArray(state.termOrder) ? state.termOrder : [];
      const nextOrder =
        order.length > 0
          ? order.map((tid) => (tid === customId ? libraryId : tid))
          : order;
      return {
        ...state,
        customTerms: customs.filter((t) => t.id !== customId),
        terms: nextTerms,
        termOrder: nextOrder,
      };
    }

    // Update display order (drag-to-reorder result).
    case 'REORDER_TERMS': {
      const { orderedIds } = action;
      if (!Array.isArray(orderedIds)) return state;
      return { ...state, termOrder: orderedIds };
    }

    // ── Reset ──
    case 'RESET':
      return createInitialState();

    case 'LOAD_FROM_JOB': {
      const merged = { ...state, ...action.payload, linkedJobId: action.jobId };
      const maxId = (merged.lines || []).reduce((m, l) => Math.max(m, Number(l.id) || 0), 0);
      merged.nextLineId = Math.max(merged.nextLineId || 1, maxId + 1);
      merged.lines = backfillSids(merged.lines);
      return merged;
    }

    case 'LOAD_FROM_SNAPSHOT': {
      const { snapshot, job } = action;
      const jobType = (job.jobType || '').toLowerCase();
      const jobMode = jobType.includes('virtour')
        ? 'virtour'
        : (jobType.includes('real estate') ? 're' : 'custom');
      const lines = (snapshot.deliverableLines || []).map((dl, i) => ({
        id: i + 1,
        sid: dl.sid || lineSid(dl, i), // snapshot lines are already addon-free
        desc: dl.desc || dl.name || '',
        qty: dl.qty ?? 1,
        rate: dl.rate ?? dl.price ?? dl.amount ?? 0,
        source: dl.source || (snapshot.mode || jobMode),
        productId: dl.productId || null,
        categoryId: dl.categoryId || null,
        // VirTour split + VirTour's product id — absent on every other line
        ...(dl.splitPct != null ? { splitPct: dl.splitPct } : {}),
        ...(dl.vtPid ? { vtPid: dl.vtPid } : {}),
        schedule: dl.schedule || [{
          date: dl.shootDate || '',
          endDate: '',
          time: dl.shootTime || '',
          endTime: '',
          location: dl.location || '',
        }],
        deliveryDate: dl.deliveryDate || '',
        deliveryTime: dl.deliveryTime || '',
        notes: dl.notes || '',
        on: true,
        ...(dl.rateOverride ? { rateOverride: true, originalRate: dl.originalRate, originalSqft: dl.originalSqft } : {}),
      }));
      return {
        ...state,
        mode: snapshot.mode || jobMode,
        workflowType: job.workflowType || snapshot.workflowType || 'standard',
        contactId: job.contactId || state.contactId || '',
        client: job.clientName || state.client,
        contact: job.contactName || '',
        email: job.contactEmail || state.email,
        phone: job.contactPhone || '',
        address: job.location || '',
        sqft: snapshot.sqft || job.sqft || '',
        quoteNumber: snapshot.quoteNumber || state.quoteNumber,
        convertedFrom: snapshot.convertedFrom || state.convertedFrom,
        rePriceVersion: rgNormalizeVersion(snapshot.rePriceVersion),
        lines,
        nextLineId: lines.length + 1,
        linkedJobId: action.jobId,
      };
    }

    case 'LOAD_FROM_QUOTE': {
      const q = action.quote;
      // An RE quote saved before the price lists split has no version: it was
      // priced off the Old list and reopens there, never repriced under the
      // client. (Other modes don't read it; converting to RE starts on New.)
      const rePriceVersion = rgNormalizeVersion(q.calcSnapshot?.rePriceVersion);
      // Quote lines include re-addons — deterministic sid backfill indexes over
      // the non-addon sequence only (must match calc_snapshot.deliverableLines)
      let _naIdx = 0;
      const lines = (q.deliverableLines || []).map((dl, i) => {
        const isAddon = dl.source === 're-addon';
        const naIdx = _naIdx;
        if (!isAddon) _naIdx++;
        return {
        id: i + 1,
        sid: dl.sid || (isAddon ? genSid() : lineSid(dl, naIdx)),
        desc: dl.desc || dl.name || '',
        detail: dl.detail || '',
        qty: dl.qty ?? 1,
        rate: dl.rate ?? dl.price ?? dl.amount ?? 0,
        dur: dl.dur || 0,
        source: dl.source || q.mode || state.mode,
        productId: dl.productId || null,
        categoryId: dl.categoryId || null,
        // VirTour split + VirTour's product id — absent on every other line
        ...(dl.splitPct != null ? { splitPct: dl.splitPct } : {}),
        ...(dl.vtPid ? { vtPid: dl.vtPid } : {}),
        // A VirTour line with no recorded version was quoted before the Sep 2026
        // reprice, so it reloads as legacy: reopening a sent quote must never
        // move its prices under the client.
        ...(dl.source === 'virtour' ? { priceVersion: vtNormalizeVersion(dl.priceVersion) } : {}),
        // RE lines sell at the quote's list, whatever an older save left here.
        ...(dl.source === 're' || dl.source === 're-addon' ? { priceVersion: rePriceVersion } : {}),
        schedule: dl.schedule || [{
          date: dl.shootDate || '',
          endDate: '',
          time: dl.shootTime || '',
          endTime: '',
          location: dl.location || '',
        }],
        deliveryDate: dl.deliveryDate || '',
        deliveryTime: dl.deliveryTime || '',
        notes: dl.notes || '',
        on: true,
        ...(dl.rateOverride ? { rateOverride: true, originalRate: dl.originalRate, originalSqft: dl.originalSqft } : {}),
        };
      });
      const shootDates = (q.shootDates || []).map((d, i) => {
        if (typeof d === 'string') return { id: i + 1, date: d, label: '', startTime: '' };
        return { id: d.id ?? i + 1, date: d.date || '', label: d.label || '', startTime: d.startTime || '' };
      });
      const addons = {};
      (q.addOns || []).forEach((a) => { addons[a] = true; });
      // Quote terms: accept both legacy string[] (IDs) and new object[] snapshots.
      // Builder state stores { id, on } — rebuild that from either shape.
      const enabledTermIds = new Set();
      if (Array.isArray(q.terms)) {
        if (isLegacyTerms(q.terms)) {
          for (const id of q.terms) enabledTermIds.add(id);
        } else {
          for (const t of q.terms) {
            if (t && t.id && (t.on === undefined || t.on === true)) enabledTermIds.add(t.id);
          }
        }
      }
      const terms = enabledTermIds.size > 0
        ? (() => {
            // Union of: DEFAULT_TERMS IDs + any enabled library-added IDs from
            // the snapshot (e.g. lib_1) that aren't already in defaults.
            // Custom_* IDs are excluded — those route into state.customTerms
            // via the cs.customTerms restore below.
            const knownIds = new Set(DEFAULT_TERMS.map((t) => t.id));
            const conditionalIds = new Set(
              DEFAULT_TERMS.filter((t) => t.type === 'conditional').map((t) => t.id)
            );
            const extras = [...enabledTermIds].filter(
              (id) => !knownIds.has(id) && !String(id).startsWith('custom_')
            );
            const allIds = [...DEFAULT_TERMS.map((t) => t.id), ...extras];
            // Mark conditional entries `userSet` so the reopened quote keeps the
            // exact clauses it shipped with, regardless of current service triggers.
            return allIds.map((id) => ({
              id,
              on: enabledTermIds.has(id),
              ...(conditionalIds.has(id) ? { userSet: true } : {}),
            }));
          })()
        : state.terms;

      // Rebuild selectedProducts from catalog lines (RE products, VirTour
      // products + packages — both catalogs key their selection the same way).
      // The value is the line's price version, so the rebuild reprices at the
      // version the quote was sold at; RE has one price set, and its value is
      // only ever read for truthiness.
      const selectedProducts = {};
      lines.filter(l => l.productId && (l.source === 're' || l.source === 'virtour')).forEach(l => {
        selectedProducts[l.productId] = l.source === 'virtour'
          ? vtNormalizeVersion(l.priceVersion)
          : VT_CURRENT_VERSION;
      });
      // An RE line's count lives on the line (a website order's three reels), so
      // the next rebuild keeps it.
      const productQuantities = {};
      lines.filter(l => l.productId && l.source === 're' && Number(l.qty) > 1).forEach(l => {
        productQuantities[l.productId] = Number(l.qty);
      });

      // Restore builder fields from calcSnapshot (they aren't in the quotes table)
      const cs = q.calcSnapshot || {};

      // Restore add-on rate overrides as synthetic re-addon entries so the next
      // rebuildRELines() call preserves them (it matches by productId+source).
      // Skip orphans (addon not currently selected) and unknown ids.
      const addonOverrides = cs.addonOverrides || {};
      Object.keys(addonOverrides).forEach((addonId) => {
        const o = addonOverrides[addonId] || {};
        if (o.originalRate == null) return;
        if (!addons[addonId]) return; // orphan: addon was unchecked
        // The quote's lines already hold the add-on (deliverable_lines stores
        // every active line): flag the override there. A second copy billed the
        // add-on again on every reopen-and-save.
        const own = lines.find((l) => l.source === 're-addon' && l.productId === addonId);
        if (own) {
          if (!own.rateOverride) Object.assign(own, { rateOverride: true, originalRate: o.originalRate, originalSqft: o.originalSqft });
          return;
        }
        const addonMeta = rgFindAddon(addonId, rePriceVersion);
        if (!addonMeta) return; // unknown id
        const qty = addonMeta.perImage ? ((cs.addonQuantities || {})[addonId] || 1) : 1;
        lines.push({
          id: lines.length + 1,
          sid: genSid(),
          desc: addonMeta.name,
          detail: addonMeta.desc,
          qty,
          rate: o.rate,
          dur: addonMeta.durAdd || 0,
          source: 're-addon',
          productId: addonId,
          priceVersion: rePriceVersion,
          categoryId: null,
          rateOverride: true,
          originalRate: o.originalRate,
          originalSqft: o.originalSqft,
          schedule: [{ date: '', endDate: '', time: '', endTime: '', location: '' }],
          deliveryDate: '',
          deliveryTime: '',
          shootDateId: null,
        });
      });

      // Discount intent: prefer explicit calcSnapshot value, fall back to
      // legacy inference for old quotes that didn't store discountType.
      const csType = cs.discountType;
      const csValue = cs.discountValue;
      const legacyPct = parseFloat(q.discountPct) || 0;
      const legacyAmt = parseFloat(q.discountAmt) || 0;
      const discountType = csType === 'pct' || csType === 'flat'
        ? csType
        : (legacyPct > 0 ? 'pct' : 'flat');
      const discountValue = csType === 'pct' || csType === 'flat'
        ? (Number(csValue) || 0)
        : (legacyPct > 0 ? legacyPct : legacyAmt);

      // Restore per-quote terms editing intent from calcSnapshot. Custom
      // clauses (cs.customTerms) get their `on` state from the resolved
      // `enabledTermIds` set above so toggled-off custom terms stay off after
      // reload. nextCustomTermId is computed from the highest existing id so
      // future ADD_CUSTOM_TERM dispatches don't collide.
      const termOverrides = (cs.termOverrides && typeof cs.termOverrides === 'object')
        ? cs.termOverrides
        : {};
      const csCustomTerms = Array.isArray(cs.customTerms) ? cs.customTerms : [];
      const customTerms = csCustomTerms.map((t) => ({
        id: t.id,
        label: t.label || '',
        body: t.body || '',
        note: t.note || '',
        on: enabledTermIds.has(t.id) ? true : (t.on !== false),
      }));
      const termOrder = Array.isArray(cs.termOrder) ? cs.termOrder : [];
      const maxCustomId = customTerms.reduce((m, t) => {
        const match = String(t.id).match(/^custom_(\d+)$/);
        return match ? Math.max(m, parseInt(match[1], 10)) : m;
      }, 0);
      const nextCustomTermId = maxCustomId + 1;

      return {
        ...state,
        mode: q.mode || state.mode,
        workflowType: q.workflowType || q.calcSnapshot?.workflowType || 'standard',
        contactId: q.contactId || state.contactId || '',
        client: q.clientName || state.client,
        contact: cs.contact || state.contact,
        email: q.clientEmail || state.email,
        phone: cs.phone || state.phone,
        address: cs.address || state.address,
        sqft: cs.sqft != null ? String(cs.sqft) : state.sqft,
        originator: cs.originator || state.originator,
        projectName: cs.projectName || state.projectName,
        internalNotes: cs.internalNotes || state.internalNotes,
        convertedFrom: cs.convertedFrom || state.convertedFrom,
        propertyType: cs.propertyType || state.propertyType,
        bedrooms: cs.bedrooms || state.bedrooms,
        bathrooms: cs.bathrooms || state.bathrooms,
        preparedBy: cs.preparedBy || state.preparedBy,
        addonQuantities: cs.addonQuantities || state.addonQuantities,
        quoteNumber: q.quoteNumber || state.quoteNumber,
        validUntil: q.expiresAt ? q.expiresAt.split('T')[0] : state.validUntil,
        notes: q.notes || state.notes,
        discountType,
        discountValue,
        discountReason: q.discountReason || '',
        lines,
        nextLineId: lines.length + 1,
        shootDates,
        nextShootDateId: shootDates.length + 1,
        selectedAddons: addons,
        selectedProducts,
        productQuantities,
        rePriceVersion,
        terms,
        termOverrides,
        customTerms,
        termOrder,
        nextCustomTermId,
        linkedJobId: action.jobId,
        sourceQuoteId: q.id,
        sourceQuoteRevision: q.revision || 1,
        sourceQuoteStatus: q.status || 'draft',
      };
    }

    // After a Save Draft creates a NEW quote, adopt it as the source so the
    // next save UPDATES it instead of inserting a duplicate row.
    case 'MARK_SAVED_QUOTE':
      return {
        ...state,
        sourceQuoteId: action.id ?? state.sourceQuoteId,
        sourceQuoteStatus: 'draft',
        sourceQuoteRevision: action.revision ?? state.sourceQuoteRevision,
        linkedJobId: action.jobId ?? state.linkedJobId,
      };

    default:
      return state;
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useQuoteBuilder() {
  const [state, dispatch] = useReducer(quoteReducer, null, createInitialState);
  // Settings drives the effective term library (DEFAULT_TERMS overlaid with
  // settings.data.quoteTerms overrides). When settings is still loading,
  // getEffectiveLibrary({}) returns DEFAULT_TERMS only, so the builder works
  // even before settings hydrate.
  const { data: settings } = useSettings();

  // Learned VirTour splits: { [productOrPackageId]: pct }, written back on every
  // successful quote save (QuoteBuilder) so the next quote prefills itself.
  const vtSplitDefaults = useMemo(() => {
    const raw = settings?.data?.virtourSplits;
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  }, [settings]);

  // Auto-save draft to sessionStorage on state change
  useEffect(() => {
    try {
      if (state.client || state.lines.length > 0) {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state));
      } else {
        sessionStorage.removeItem(DRAFT_KEY);
      }
    } catch { /* storage full or unavailable */ }
  }, [state]);

  // Wrap dispatch to clear draft on RESET
  const wrappedDispatch = useCallback((action) => {
    if (action.type === 'RESET') {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* */ }
    }
    dispatch(action);
  }, []);

  // ── Rebuild RE lines from selected products + sq ft ──
  // Order-preserving: walks existing state.lines first to honour any manual
  // reorder the user has performed, then prunes lines that are no longer
  // valid (deselected products, addons that no longer apply or are excluded),
  // and finally appends any newly-selected items at the end. Newly-added
  // addons are placed directly after the first parent product whose category
  // matches the addon's `applies` (in the catalog-declared order, so the
  // first applicable category wins regardless of toggle order).
  const rebuildRELines = useCallback(() => {
    const sqft = parseInt(state.sqft) || 0;

    // ── VirTour mode ──
    // The quote mirrors a VirTour order: every selected product or package
    // reprices from VIRTOUR_DB at the current sq ft tier. Existing lines are
    // spread forward so per-line fields the catalog doesn't own survive the
    // reprice. Custom lines pass through verbatim, exactly as in RE.
    if (state.mode === 'virtour') {
      // One selection map for both shapes — package ids resolve first.
      const vtSelected = new Map();
      for (const id of Object.keys(state.selectedProducts)) {
        const pkg = findVtPackage(id);
        if (pkg) { vtSelected.set(id, { pkg }); continue; }
        const found = findVtProduct(id);
        if (found) vtSelected.set(id, found);
      }

      // An add-on-priced product bills at its "+" rate only when it rides along
      // with another VirTour item — a package, or a product that isn't itself
      // add-on-priced. Alone on the order it bills at the standalone price.
      const vtRidesAlong = (id) => {
        for (const [otherId, entry] of vtSelected) {
          if (otherId === id) continue;
          if (entry.pkg || entry.product.addonPrice === undefined) return true;
        }
        return false;
      };

      const buildVtEntry = (id, existing) => {
        const entry = vtSelected.get(id);
        if (!entry) return null;
        const item = entry.pkg || entry.product;
        // A selection carrying no version was made before the reprice, so it
        // prices as legacy — reopening old work never marks it up silently.
        const requested = vtNormalizeVersion(state.selectedProducts[id]);
        // A product VirTour launched after the reprice has no old price. The
        // picker offers it no old column, but never stamp a line 'legacy' while
        // pricing it off the current sheet if one ever reaches here.
        const version = vtExistsAt(item, requested) ? requested : VT_CURRENT_VERSION;
        const sqftChanged = existing?.originalSqft != null && existing.originalSqft !== sqft;
        // Deliberately moving a line to the other price column is itself a
        // pricing decision, so it outranks a rate typed against the old one.
        const versionChanged = !!existing && vtNormalizeVersion(existing.priceVersion) !== version;
        const keepOverride = existing?.rateOverride && !sqftChanged && !versionChanged;
        let desc, detail, rate, categoryId, vtPid;
        if (entry.pkg) {
          const tier = vtGetPackagePrice(entry.pkg, sqft, version);
          desc = vtGetName(entry.pkg, version);
          detail = entry.pkg.includes.join(' · ');
          rate = tier.price;
          categoryId = null;
          vtPid = tier.pid;
        } else {
          const asAddon = entry.product.addonPrice !== undefined && vtRidesAlong(id);
          desc = vtGetName(entry.product, version);
          detail = entry.product.desc || '';
          rate = asAddon ? getVtAddonPrice(entry.product, sqft, version) : vtGetPrice(entry.product, sqft, version);
          categoryId = entry.category.id;
          vtPid = vtGetPid(entry.product, sqft, asAddon);
        }
        // A hand-edited desc survives the reprice (like qty), but a desc the
        // catalog set must follow a version flip: the reprice renamed two
        // packages, and this desc is what reaches the client PDF and the payout
        // ledger, so a stale one names a product at a price it never sold for.
        const descIsCatalogSet = !existing?.desc
          || existing.desc === vtGetName(item, VT_CURRENT_VERSION)
          || existing.desc === vtGetName(item, VT_LEGACY_VERSION);

        return {
          ...(existing || {}),
          sid: existing?.sid || genSid(),
          desc: descIsCatalogSet ? desc : existing.desc,
          detail: existing?.detail ?? detail,
          qty: existing?.qty ?? 1,
          rate: keepOverride ? existing.rate : rate,
          // Split follows the line once it exists (a cleared split stays clear
          // through a reprice); only a brand-new line takes the learned default.
          splitPct: existing ? (existing.splitPct ?? null) : (vtSplitDefaults[id] ?? null),
          dur: 0,
          source: 'virtour',
          productId: id,
          vtPid,
          priceVersion: version,
          categoryId,
          schedule: existing?.schedule || [{ date: '', endDate: '', time: '', endTime: '', location: state.address || '' }],
          deliveryDate: existing?.deliveryDate || '',
          deliveryTime: existing?.deliveryTime || 'EOD',
          shootDateId: existing?.shootDateId || null,
          ...(keepOverride
            ? { rateOverride: true, originalRate: existing.originalRate, originalSqft: existing.originalSqft }
            : { rateOverride: false, originalRate: undefined, originalSqft: undefined }),
        };
      };

      // Pass 1: walk existing lines in order, reprice what's still selected,
      // drop the rest. Non-VirTour lines (customs) are kept as-is.
      const seenVtIds = new Set();
      const nextVt = [];
      for (const line of state.lines) {
        if (line.source !== 'virtour') {
          nextVt.push(line);
          continue;
        }
        const pid = line.productId;
        if (!pid || !vtSelected.has(pid) || seenVtIds.has(pid)) continue;
        const fresh = buildVtEntry(pid, line);
        if (!fresh) continue;
        seenVtIds.add(pid);
        nextVt.push({ ...fresh, id: line.id });
      }

      // Pass 2: append newly-selected items just after the last non-custom line
      // so manually repositioned custom lines stay where the user put them.
      const usedVtIds = new Set(nextVt.map((l) => Number(l.id) || 0));
      let nextVtIdSeq = (usedVtIds.size ? Math.max(...usedVtIds) : 0) + 1;
      const findVtAppendIdx = () => {
        for (let i = nextVt.length - 1; i >= 0; i--) {
          if (nextVt[i].source !== 'custom') return i + 1;
        }
        return nextVt.length;
      };
      for (const id of vtSelected.keys()) {
        if (seenVtIds.has(id)) continue;
        const fresh = buildVtEntry(id, null);
        if (!fresh) continue;
        seenVtIds.add(id);
        while (usedVtIds.has(nextVtIdSeq)) nextVtIdSeq++;
        const newId = nextVtIdSeq++;
        usedVtIds.add(newId);
        nextVt.splice(findVtAppendIdx(), 0, { ...fresh, id: newId });
      }

      const sameVtRefs = nextVt.length === state.lines.length
        && nextVt.every((l, i) => l === state.lines[i]);
      if (sameVtRefs) return;

      dispatch({ type: 'SET_LINES', lines: nextVt });
      return;
    }

    // Compute current selection sets. Everything prices off the quote's price
    // list (the Old / New tab); a pick that list doesn't sell builds no line.
    const reVersion = rgNormalizeVersion(state.rePriceVersion);
    const selectedProdIds = new Set(Object.keys(state.selectedProducts));
    const selectedAddonIds = Object.keys(state.selectedAddons);

    // Compute which addons currently pass all gates (applies, excludeProducts,
    // ONCE_PER_QUOTE, and the New list's size and requirement gates). We use
    // the *current* selectedProducts to gate, not the post-rebuild list, so
    // deselecting all applicable products also drops the addon line. Prices
    // that depend on another add-on read this set, not the ticks.
    const validAddonIds = rgBillableAddonIds(state.selectedProducts, state.selectedAddons, sqft, reVersion);
    const priceCtx = rgPriceContext([...selectedProdIds], validAddonIds);

    // Helper: build a fresh RE-product line entry (no id assigned yet).
    const buildREEntry = (productId, existing) => {
      const found = rgFindProduct(productId, reVersion);
      if (!found) return null;
      const { category, product } = found;
      const { price, dur } = rgGetPrice(product, sqft, reVersion, priceCtx);
      const sqftChanged = existing?.originalSqft != null && existing.originalSqft !== sqft;
      // Moving the quote to the other price list is itself a pricing decision,
      // so it outranks a rate typed against the old one (as VirTour does).
      const versionChanged = !!existing && rgNormalizeVersion(existing.priceVersion) !== reVersion;
      const keepOverride = existing?.rateOverride && !sqftChanged && !versionChanged;
      const qty = state.productQuantities?.[productId] || 1;
      return {
        sid: existing?.sid || genSid(),
        desc: product.name,
        detail: product.desc,
        qty,
        // A New-list item quoted rather than priced at this size comes in at $0
        // for the rate to be typed in; `quoted` keeps it amber while the rate
        // is empty, since a $0 line is left off the quote.
        rate: keepOverride ? existing.rate : (price ?? 0),
        quoted: price == null,
        // Each extra copy adds half the product's time, more cuts from the same
        // setup, as the website times it (Cyprian, 2026-09-29).
        dur: qty > 1 ? (dur || 0) + Math.round((dur || 0) * 0.5 * (qty - 1)) : dur,
        source: 're',
        productId: product.id,
        priceVersion: reVersion,
        categoryId: category.id,
        schedule: existing?.schedule || [{ date: '', endDate: '', time: '', endTime: '', location: state.address || '' }],
        deliveryDate: existing?.deliveryDate || '',
        deliveryTime: existing?.deliveryTime || 'EOD',
        shootDateId: existing?.shootDateId || null,
        ...(keepOverride ? { rateOverride: true, originalRate: existing.originalRate, originalSqft: existing.originalSqft } : {}),
      };
    };

    // Helper: build a fresh re-addon line entry (no id assigned yet).
    const buildAddonEntry = (addonId, existing) => {
      const addon = rgFindAddon(addonId, reVersion);
      if (!addon) return null;
      const qty = addon.perImage ? (state.addonQuantities[addonId] || 1) : 1;
      // A size change reprices a size-priced add-on, as it does a product: a
      // rate typed at the old size was typed against a price this size doesn't
      // have, and Reset would bring that stale price back, or $0 from a quoted
      // size, which leaves the line off the quote with no amber. A flat add-on
      // keeps its typed rate whatever the size.
      const sqftChanged = !!addon.sizePrices && existing?.originalSqft != null && existing.originalSqft !== sqft;
      const versionChanged = !!existing && rgNormalizeVersion(existing.priceVersion) !== reVersion;
      const keepOverride = existing?.rateOverride && !sqftChanged && !versionChanged;
      const price = rgAddonPrice(addon, sqft, priceCtx);
      return {
        sid: existing?.sid || genSid(),
        desc: addon.name,
        detail: addon.desc,
        qty,
        rate: keepOverride ? existing.rate : (price ?? 0),
        quoted: price == null,
        // A counted add-on's time is per copy (each Additional Simple Reel).
        dur: (addon.durAdd || 0) * qty,
        source: 're-addon',
        productId: addonId,
        priceVersion: reVersion,
        categoryId: null,
        schedule: existing?.schedule || [{ date: '', endDate: '', time: '', endTime: '', location: '' }],
        deliveryDate: existing?.deliveryDate || '',
        deliveryTime: existing?.deliveryTime || '',
        shootDateId: existing?.shootDateId || null,
        ...(keepOverride ? { rateOverride: true, originalRate: existing.originalRate, originalSqft: existing.originalSqft } : {}),
      };
    };

    // Pass 1: walk existing state.lines in current order, keep what's still
    // valid (refreshed from catalog), drop the rest. Custom lines are always
    // kept verbatim. Track which RE/addon ids we've consumed so we can append
    // any newly-selected items at the end.
    const seenProductIds = new Set();
    const seenAddonIdsInPass1 = new Set();
    const next = [];
    for (const line of state.lines) {
      if (line.source === 'custom') {
        next.push(line); // preserve as-is (id, desc, schedule, etc.)
        continue;
      }
      if (line.source === 're') {
        const pid = line.productId;
        if (!pid || !selectedProdIds.has(pid) || seenProductIds.has(pid)) continue;
        const fresh = buildREEntry(pid, line);
        if (!fresh) continue;
        seenProductIds.add(pid);
        // Preserve existing id so React keys stay stable (no input focus loss).
        next.push({ ...fresh, id: line.id });
        continue;
      }
      if (line.source === 're-addon') {
        const aid = line.productId;
        if (!aid || !validAddonIds.has(aid) || seenAddonIdsInPass1.has(aid)) continue;
        const fresh = buildAddonEntry(aid, line);
        if (!fresh) continue;
        seenAddonIdsInPass1.add(aid);
        next.push({ ...fresh, id: line.id });
        continue;
      }
      // Unknown source — keep as-is rather than silently drop.
      next.push(line);
    }

    // Pass 2: append newly-selected RE products that weren't already present.
    // ID allocation must avoid colliding with anything already in `next`.
    const usedIds = new Set(next.map((l) => Number(l.id) || 0));
    let nextIdSeq = (usedIds.size ? Math.max(...usedIds) : 0) + 1;
    const allocId = () => {
      while (usedIds.has(nextIdSeq)) nextIdSeq++;
      const out = nextIdSeq++;
      usedIds.add(out);
      return out;
    };

    // Helper: insert position is "just after the last non-custom line" so that
    // newly-selected items respect any manual reordering the user has done.
    // If there are no non-custom lines, append at end.
    const findAppendIdx = () => {
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].source !== 'custom') return i + 1;
      }
      return next.length;
    };

    for (const productId of Object.keys(state.selectedProducts)) {
      if (seenProductIds.has(productId)) continue;
      const fresh = buildREEntry(productId, null);
      if (!fresh) continue;
      seenProductIds.add(productId);
      next.splice(findAppendIdx(), 0, { ...fresh, id: allocId() });
    }

    // Pass 3: place newly-selected addons. For each one, find its preferred
    // parent by walking `addon.applies` in catalog-declared order (so the
    // catalog author's intent wins, not the user's toggle order). Insert the
    // addon immediately after that parent (or after the parent's existing
    // trailing addons, so multiple addons under the same parent stay grouped).
    for (const addonId of selectedAddonIds) {
      if (!validAddonIds.has(addonId)) continue;
      if (seenAddonIdsInPass1.has(addonId)) continue;
      const addon = rgFindAddon(addonId, reVersion);
      const fresh = buildAddonEntry(addonId, null);
      if (!fresh || !addon) continue;
      const newEntry = { ...fresh, id: allocId() };

      // Walk `applies` in declared order, find first parent in `next` matching
      // that category id. If none, append at end (before custom-line tail).
      let parentIdx = -1;
      for (const catId of addon.applies) {
        parentIdx = next.findIndex((l) => l.source === 're' && l.categoryId === catId);
        if (parentIdx !== -1) break;
      }
      if (parentIdx === -1) {
        // No parent visible — drop in just after the last non-custom line so
        // that customs the user has moved to the top stay at the top.
        next.splice(findAppendIdx(), 0, newEntry);
        continue;
      }
      // Find the end of this parent's existing trailing-addon block.
      let insertAt = parentIdx + 1;
      while (insertAt < next.length && next[insertAt].source === 're-addon') insertAt++;
      next.splice(insertAt, 0, newEntry);
    }

    // Skip dispatch if nothing actually changed (avoids redundant render +
    // sessionStorage write, and prevents an infinite loop because this
    // callback depends on state.lines).
    const sameLength = next.length === state.lines.length;
    const sameRefs = sameLength && next.every((l, i) => l === state.lines[i]);
    if (sameRefs) return;

    dispatch({ type: 'SET_LINES', lines: next });
  }, [state.mode, state.rePriceVersion, state.selectedProducts, state.productQuantities, state.selectedAddons, state.addonQuantities, state.sqft, state.address, state.lines, vtSplitDefaults]);

  // ── Computed values ──
  const computed = useMemo(() => {
    const activeLines = state.lines.filter((l) => l.desc && (l.rate > 0 || l.source === 'custom'));

    const subtotal = activeLines.reduce((sum, l) => sum + (l.qty || 0) * (l.rate || 0), 0);

    let discountAmount = 0;
    if (state.discountType === 'pct' && state.discountValue > 0) {
      discountAmount = subtotal * (state.discountValue / 100);
    } else if (state.discountType === 'flat' && state.discountValue > 0) {
      discountAmount = Math.min(state.discountValue, subtotal);
    }

    const total = Math.max(0, subtotal - discountAmount);

    const estimatedDuration = activeLines.reduce((sum, l) => sum + (l.dur || 0), 0);

    // Categories with selected products
    const selectedCategoryIds = [
      ...new Set(
        activeLines
          .filter((l) => l.categoryId)
          .map((l) => l.categoryId)
      ),
    ];

    // VirTour: `subtotal` above is VirTour's retail. What we quote is our cut of
    // it, so retail -> split -> payout is the whole transform (no discount) and
    // both the subtotal and the total ARE the payout.
    const isVirtour = state.mode === 'virtour';
    const vt = isVirtour
      ? vtTotals(activeLines)
      : { retailTotal: 0, payoutTotal: 0, blendedPct: 0 };

    return {
      activeLines,
      subtotal: isVirtour ? vt.payoutTotal : subtotal,
      discountAmount: isVirtour ? 0 : Math.round(discountAmount * 100) / 100,
      total: isVirtour ? vt.payoutTotal : Math.round(total * 100) / 100,
      estimatedDuration,
      selectedCategoryIds,
      lineCount: activeLines.length,
      retailTotal: vt.retailTotal,
      payoutTotal: vt.payoutTotal,
      blendedPct: vt.blendedPct,
    };
  }, [state.lines, state.mode, state.discountType, state.discountValue]);

  // ── Active terms (library + custom, with per-quote overrides applied) ──
  // Merges:
  //   1. DEFAULT_TERMS (the library) — patched with per-quote overrides + state.terms toggle
  //   2. state.customTerms (ad-hoc clauses just on this quote)
  // Then reorders to match state.termOrder if set; otherwise keeps library-then-custom order.
  // Conditional library terms auto-deactivate when their trigger categories are absent.
  const activeTerms = useMemo(() => {
    const catSet = new Set(computed.selectedCategoryIds);
    const overrides = state.termOverrides || {};
    const library = getEffectiveLibrary(settings);

    const libraryTerms = library.map((term) => {
      const stateEntry = state.terms.find((t) => t.id === term.id);
      const triggers = Array.isArray(term.triggerCategories) ? term.triggerCategories : [];

      // Conditional terms: the trigger categories set only the DEFAULT on-state
      // (auto-enable when a matching service is selected). An explicit user
      // toggle (`userSet`) always wins, so a conditional clause can be turned on
      // with no matching service, or off despite one. Non-conditional terms (and
      // conditionals with no triggers) fall back to the stored/library `on`.
      let isActive;
      if (term.type === 'conditional' && triggers.length > 0) {
        isActive = stateEntry?.userSet ? !!stateEntry.on : triggers.some((c) => catSet.has(c));
      } else {
        isActive = stateEntry?.on ?? term.on ?? false;
      }

      const ov = overrides[term.id];
      // Per-quote override beats library override badge
      const perQuoteOverridden = !!ov && (ov.label !== undefined || ov.body !== undefined);

      return {
        ...term,
        label: ov?.label !== undefined ? ov.label : term.label,
        body: ov?.body !== undefined ? ov.body : term.body,
        note: ov?.note !== undefined ? ov.note : (term.note || ''),
        triggerCategories: triggers,
        on: isActive,
        // `source` distinguishes default/library/custom; library-added entries
        // already carry `source: 'library'` from getEffectiveLibrary so we
        // preserve it via the spread above.
        source: term.source || 'default',
        // Per-quote override badge (orange). Settings-level overrides show as
        // "Customized" in the settings editor, not on the builder.
        isOverridden: perQuoteOverridden,
      };
    });

    const customTerms = (state.customTerms || []).map((t) => ({
      id: t.id,
      label: t.label || '',
      body: t.body || '',
      note: t.note || '',
      type: 'custom',
      triggerCategories: [],
      on: t.on !== false,
      source: 'custom',
      isOverridden: false,
    }));

    const all = [...libraryTerms, ...customTerms];

    // Apply user-defined order if any. Unknown IDs in termOrder are dropped;
    // IDs missing from termOrder are appended in library-then-custom order.
    if (Array.isArray(state.termOrder) && state.termOrder.length > 0) {
      const byId = new Map(all.map((t) => [t.id, t]));
      const ordered = [];
      const used = new Set();
      for (const id of state.termOrder) {
        const t = byId.get(id);
        if (t && !used.has(id)) { ordered.push(t); used.add(id); }
      }
      for (const t of all) {
        if (!used.has(t.id)) ordered.push(t);
      }
      return ordered;
    }

    return all;
  }, [state.terms, state.termOverrides, state.customTerms, state.termOrder, computed.selectedCategoryIds, settings]);

  // ── Build job data from current quote state ──
  // Must match the jobs table schema in Supabase (see DATA_MODEL.md)
  const buildJobData = useCallback((existingCalcSnapshot) => {
    const deliverableLines = computed.activeLines
      .filter((l) => l.source !== 're-addon')
      .map((l, i) => ({
        sid: l.sid || lineSid(l, i), // stable identity — pay allocations key on it
        desc: l.desc,
        source: l.source,
        deliveryDate: l.deliveryDate || '',
        deliveryTime: l.deliveryTime || 'EOD',
        // On a VirTour quote `amount` is OUR revenue (the payout share), not
        // VirTour's retail — the service-schedule emails and the pay
        // calculator's per-item allocation both read it as money we earned.
        amount: state.mode === 'virtour' ? vtLinePayout(l) : (l.qty || 0) * (l.rate || 0),
        qty: l.qty || 1,
        rate: l.rate || 0,
        dur: l.dur || 0,
        productId: l.productId || null,
        categoryId: l.categoryId || null,
        // On a VirTour quote every line carries its split + VirTour's own
        // product id, so the payout stays reconstructable from the snapshot
        // alone (custom lines on the order are split the same way). The price
        // version rides along so reopening the quote reprices at the set it was
        // sold at, not at whatever the catalog ships today.
        ...(state.mode === 'virtour'
          ? {
              splitPct: l.splitPct ?? null,
              vtPid: l.vtPid || null,
              priceVersion: vtNormalizeVersion(l.priceVersion),
            }
          : {}),
        // RE lines carry the price list they were sold off (the quote's).
        ...(state.mode === 're' && l.source === 're'
          ? { priceVersion: rgNormalizeVersion(state.rePriceVersion) }
          : {}),
        schedule: l.schedule || [],
        // Compat aliases from schedule[0]
        location: (l.schedule || [])[0]?.location || '',
        shootDate: (l.schedule || [])[0]?.date || '',
        shootTime: (l.schedule || [])[0]?.time || '',
        // Price override tracking
        ...(l.rateOverride ? { rateOverride: true, originalRate: l.originalRate, originalSqft: l.originalSqft } : {}),
      }));

    // RE add-ons are deliberately absent from deliverableLines: that array is
    // sid-keyed, its deterministic sid backfill indexes over the non-addon
    // sequence only (see backfillSids / LOAD_FROM_QUOTE), and pay allocations
    // key on those sids — so admitting add-ons there would reshuffle the sids
    // of existing jobs. Their money IS in subtotal/total though, so carry them
    // separately for billing documents; otherwise the invoice PDF and the
    // service-schedule email under-describe what was actually sold.
    // The per-item pay split makes each add-on an item of its own (calcEngine
    // itemLines), keyed on its sid and listed after `afterSid`, the line it
    // follows on the quote.
    const addOnLines = [];
    let afterSid = null;
    let mainIdx = 0;
    computed.activeLines.forEach((l) => {
      if (l.source !== 're-addon') {
        afterSid = l.sid || lineSid(l, mainIdx); // same sid deliverableLines gets
        mainIdx++;
        return;
      }
      addOnLines.push({
        sid: l.sid || addonSid(l, addOnLines.length),
        productId: l.productId || null,
        afterSid,
        desc: l.desc,
        detail: l.detail || '',
        qty: l.qty || 1,
        rate: l.rate || 0,
        amount: (l.qty || 0) * (l.rate || 0),
        source: l.source,
      });
    });

    // Flatten all schedule entries into individual dates
    const allDates = [];
    computed.activeLines.forEach((l) => {
      (l.schedule || []).forEach((s) => {
        if (!s.date) return;
        if (s.endDate && s.endDate > s.date) {
          const d = new Date(s.date + 'T00:00:00');
          const end = new Date(s.endDate + 'T00:00:00');
          while (d <= end) {
            allDates.push(toLocalDateStr(d));
            d.setDate(d.getDate() + 1);
          }
        } else {
          allDates.push(s.date);
        }
      });
    });
    const shootDatesArr = [...new Set(allDates)].sort();

    const deliveryDates = computed.activeLines
      .filter((l) => l.deliveryDate)
      .map((l) => l.deliveryDate)
      .sort();
    const lastDelivery = deliveryDates.length ? deliveryDates[deliveryDates.length - 1] : '';

    // VirTour never discounts — the split IS the transform.
    const discPct = state.mode === 'virtour'
      ? 0
      : (state.discountType === 'pct' ? state.discountValue : (computed.subtotal > 0 ? (computed.discountAmount / computed.subtotal) * 100 : 0));

    // calcSnapshot holds line items + RE data (sqft, quoteNumber, etc.)
    // Merge with existing snapshot to preserve pay data (splits, crew, savedAt)
    // Also stores builder fields not in the quotes table so LOAD_FROM_QUOTE can restore them
    // From all lines, not activeLines: an add-on hand-priced to $0 is left off
    // the quote but must keep its $0, or a reopen re-adds it at catalog price
    const addonOverrides = {};
    state.lines.forEach((l) => {
      if (l.source === 're-addon' && l.rateOverride && l.productId) {
        addonOverrides[l.productId] = {
          rate: l.rate,
          originalRate: l.originalRate,
          originalSqft: l.originalSqft,
        };
      }
    });

    const calcSnapshot = {
      ...(existingCalcSnapshot || {}),
      deliverableLines,
      addOnLines,
      quoteNumber: state.quoteNumber,
      mode: state.mode,
      workflowType: state.workflowType || 'standard',
      ...(state.mode === 're' || state.mode === 'virtour' ? { sqft: parseInt(state.sqft) || 0 } : {}),
      // Which RE price list the quote sells from, so reopening it reprices at
      // that list and never at whatever ships today.
      ...(state.mode === 're' ? { rePriceVersion: rgNormalizeVersion(state.rePriceVersion) } : {}),
      // VirTour payout ledger: what VirTour billed, what we keep, and the
      // per-line split that produced it. `total` is the payout, so this is the
      // only place the retail side of the deal survives.
      ...(state.mode === 'virtour'
        ? {
            virtour: {
              retailTotal: computed.retailTotal,
              blendedPct: computed.blendedPct,
              lines: deliverableLines.map((dl) => ({
                sid: dl.sid,
                vtPid: dl.vtPid || null,
                name: dl.desc,
                qty: dl.qty,
                rate: dl.rate,
                splitPct: dl.splitPct ?? null,
                priceVersion: dl.priceVersion,
                payout: vtLinePayout(dl),
              })),
            },
          }
        : {}),
      // Discount intent (so 'flat $100' doesn't reload as '10%')
      discountType: state.discountType,
      discountValue: state.discountValue,
      // RE add-on rate overrides (addon lines aren't in deliverableLines)
      addonOverrides,
      // Builder fields for round-trip restore
      contact: state.contact,
      phone: state.phone,
      address: state.address,
      originator: state.originator,
      projectName: state.projectName,
      internalNotes: state.internalNotes,
      // What this quote was before conversion — kept so the reference is still
      // there when the quote is reopened, and as a record of what was replaced.
      ...(state.convertedFrom ? { convertedFrom: state.convertedFrom } : {}),
      propertyType: state.propertyType,
      bedrooms: state.bedrooms,
      bathrooms: state.bathrooms,
      preparedBy: state.preparedBy,
      addonQuantities: state.addonQuantities,
      // Terms editing intent (restored on LOAD_FROM_QUOTE so editing resumes
      // mid-flow). The persisted `quotes.terms` column holds the *resolved
      // snapshot* (object[]) — these three fields hold the *intent* that
      // produced it so the builder can show "this body is overridden" /
      // custom clauses / drag order on reload.
      termOverrides: state.termOverrides || {},
      customTerms: state.customTerms || [],
      termOrder: Array.isArray(state.termOrder) ? state.termOrder : [],
    };

    return {
      title: state.mode === 're'
        ? `RE: ${state.address}`
        : (state.mode === 'virtour' ? (state.address || state.client) : (state.projectName || state.client)),
      client: state.client,
      workflowType: state.workflowType || 'standard',
      contact: state.contact || state.client,
      email: state.email,
      phone: state.phone,
      type: state.mode === 're' ? 'Real Estate' : (state.mode === 'virtour' ? 'VirTour' : 'Commercial'),
      location: state.address,
      shootDate: shootDatesArr[0] || null,
      shootDates: shootDatesArr,
      deliveryDate: lastDelivery || null,
      days: shootDatesArr.length || 0,
      revenue: Math.round(computed.total * 100) / 100,
      status: 'intake',
      originator: state.originator,
      referredBy: '',
      contactId: state.contactId || null,
      notes: state.internalNotes,
      quoteNotes: state.notes,
      propertyType: state.propertyType,
      bedrooms: state.bedrooms,
      bathrooms: state.bathrooms,
      sqft: state.mode === 're' || state.mode === 'virtour' ? (String(parseInt(state.sqft) || '') || '') : '',
      deliverables: computed.activeLines.map((l) => l.desc).join(', '),
      deliveryLink: '',
      discountPct: Math.round(discPct * 100) / 100,
      discountAmt: computed.discountAmount,
      discountReason: state.discountReason,
      // Snapshot shape — labels + bodies frozen at save time so emails / PDFs
      // / job views render the exact terms that were active when accepted.
      quoteTerms: snapshotTerms(activeTerms),
      calcSnapshot,
    };
  }, [state, computed, activeTerms]);

  return {
    state,
    dispatch: wrappedDispatch,
    computed,
    activeTerms,
    rebuildRELines,
    buildJobData,
  };
}
